import { useQueryClient } from '@tanstack/react-query';
import { SearchBlocksApiRequest } from '../../../../types/requests/blocks/search-blocks.request';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { GetBlockApiRequest } from '../../../../types/requests/blocks/get-block.request';
import {
  createBlocks,
  searchBlocks,
  addTemplateToBlock,
  removeTemplateFromBlock,
  addTemplatesToBlocks,
  getBlock,
  deleteBlock,
} from '../../api/blocks.api';
import { AddTemplateToBlockApiRequest } from '../../../../types/requests/blocks/add-template-to-block.request';
import { AddTemplatesToBlocksApiRequest } from '../../../../types/requests/blocks/add-templates-to-blocks.request';
import { RemoveTemplateFromBlockApiRequest } from '../../../../types/requests/blocks/remove-template-from-block.request';
import { BlockApiModel } from '../../../../types/models/block.model';
import { DeleteBlockApiRequest } from '../../../../types/requests/blocks/delete-block.request';

const baseQueryKey = 'block';
const searchQueryKey = 'search';

export const useGetBlock = <T extends BlockApiModel>(params: GetBlockApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getBlock<T>(params)).data,
    enabled: Boolean(params.blockId && !Number.isNaN(params.blockId)),
  });
  return { ...result, block: data } as const;
};

export const useSearchBlocks = (params: SearchBlocksApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchBlocks({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useCreateBlocks = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: FormData) => (await createBlocks(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, autodeskLayer: data }] as const;
};

export const useAddTemplateToBlock = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: AddTemplateToBlockApiRequest) => (await addTemplateToBlock(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey && (queryKey[1] as GetBlockApiRequest)?.blockId === params.blockId,
      }),
  });
  return [request, result] as const;
};

export const useAddTemplatesToBlocks = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: AddTemplatesToBlocksApiRequest) => (await addTemplatesToBlocks(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useRemoveTemplateFromBlock = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: RemoveTemplateFromBlockApiRequest) => (await removeTemplateFromBlock(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey && (queryKey[1] as GetBlockApiRequest)?.blockId === params.blockId,
      }),
  });
  return [request, result] as const;
};

export const useDeleteBlock = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteBlockApiRequest) => (await deleteBlock(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};
